import React, { useState, useRef, useCallback, useLayoutEffect, useEffect } from 'react';
import { api } from '../context/AppContext';
import { Button } from './ui/button';
import { 
  Loader2, 
  Sparkles, 
  Image, 
  Upload, 
  RefreshCw, 
  Camera, 
  Trash2,
  UtensilsCrossed
} from 'lucide-react';
import { toast } from 'sonner';

const sizeClasses = {
  card: 'h-32 w-full',
  detail: 'h-48 w-full',
  preview: 'h-40 w-full'
};

// Multiple fallback images — if one CDN fails, the next one kicks in
const FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=600&h=400&fit=crop&q=80',
];

function getFallbackImage(title) {
  // Deterministic fallback based on recipe title
  const hash = (title || 'recipe').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return FALLBACK_IMAGES[hash % FALLBACK_IMAGES.length];
}


const RecipeImage = React.memo(({ 
  recipe, 
  size = 'card', 
  showControls = true, 
  language = 'fr',
  onImageUpdate 
}) => {
  const fr = language === 'fr';
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const [displayState, setDisplayState] = useState('loading');
  const [isVisible, setIsVisible] = useState(false);
  const previousUrlRef = useRef(null);
  const pollRef = useRef(null);
  const [fallbackIndex, setFallbackIndex] = useState(0);
  
  const imageUrl = recipe?.image_url;
  const imageStatus = recipe?.image_status;

  const fullImageUrl = React.useMemo(() => {
    if (!imageUrl) return null;
    // Already an absolute URL (https://...)
    if (imageUrl.startsWith('http')) return imageUrl;
    // Relative URL — prepend backend base
    return `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
  }, [imageUrl]);

  // Poll for image status when generating
  useEffect(() => {
    if (imageStatus === 'generating' && recipe?.recipe_id) {
      const poll = async () => {
        try {
          const res = await api.get(`/recipes/${recipe.recipe_id}/image-status`);
          if (res.data.image_status === 'ready' && res.data.image_url) {
            if (onImageUpdate) {
              onImageUpdate(recipe.recipe_id, res.data.image_url, res.data.image_source);
            }
            clearInterval(pollRef.current);
          } else if (res.data.image_status === 'failed') {
            clearInterval(pollRef.current);
          }
        } catch { /* silent */ }
      };
      pollRef.current = setInterval(poll, 5000);
      return () => clearInterval(pollRef.current);
    }
  }, [imageStatus, recipe?.recipe_id, onImageUpdate]);

  // IntersectionObserver for lazy loading
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (fullImageUrl !== previousUrlRef.current) {
      previousUrlRef.current = fullImageUrl;
      setDisplayState(fullImageUrl ? 'loading' : 'empty');
    }
  }, [fullImageUrl]);

  const handleImageLoad = useCallback(() => { setDisplayState('loaded'); }, []);
  
  // BULLETPROOF: On ANY error (403, 404, network, etc.) → immediate fallback
  const handleImageError = useCallback(() => { 
    setDisplayState('error'); 
  }, []);

  // AGGRESSIVE TIMEOUT: 3 seconds max, then fallback. No grey boxes in production.
  useEffect(() => {
    if (displayState !== 'loading' || !fullImageUrl) return;
    const timeout = setTimeout(() => {
      setDisplayState((prev) => prev === 'loading' ? 'error' : prev);
    }, 3000);
    return () => clearTimeout(timeout);
  }, [displayState, fullImageUrl]);

  const handleGenerateImage = useCallback(async () => {
    if (!recipe?.recipe_id || isGenerating) return;
    setIsGenerating(true);
    try {
      const response = await api.post(`/recipes/${recipe.recipe_id}/generate-image`);
      toast.success(response.data.message);
      if (onImageUpdate) {
        onImageUpdate(recipe.recipe_id, response.data.image_url, 'ai_generated');
      }
    } catch {
      toast.error(fr ? 'Erreur lors de la generation' : 'Generation error');
    } finally {
      setIsGenerating(false);
    }
  }, [recipe?.recipe_id, isGenerating, fr, onImageUpdate]);

  const handleGetStockImage = useCallback(async () => {
    if (!recipe?.recipe_id || isGenerating) return;
    setIsGenerating(true);
    try {
      const response = await api.post(`/recipes/${recipe.recipe_id}/stock-image`);
      toast.success(response.data.message);
      if (onImageUpdate) {
        onImageUpdate(recipe.recipe_id, response.data.image_url, 'stock');
      }
    } catch {
      toast.error(fr ? 'Aucune image trouvee' : 'No image found');
    } finally {
      setIsGenerating(false);
    }
  }, [recipe?.recipe_id, isGenerating, fr, onImageUpdate]);

  const handleUploadImage = useCallback(async (file) => {
    if (!recipe?.recipe_id || !file || isUploading) return;
    setIsUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const response = await api.post(`/recipes/${recipe.recipe_id}/upload-image-data`, {
            image_data: reader.result
          });
          toast.success(response.data.message);
          if (onImageUpdate) {
            onImageUpdate(recipe.recipe_id, response.data.image_url, 'user_upload');
          }
        } catch {
          toast.error(fr ? "Erreur lors de l'upload" : 'Upload error');
        } finally {
          setIsUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch {
      toast.error(fr ? "Erreur lors de l'upload" : 'Upload error');
      setIsUploading(false);
    }
  }, [recipe?.recipe_id, isUploading, fr, onImageUpdate]);

  const handleRemoveImage = useCallback(async () => {
    if (!recipe?.recipe_id) return;
    try {
      await api.delete(`/recipes/${recipe.recipe_id}/image`);
      toast.success(fr ? 'Image supprimee' : 'Image removed');
      if (onImageUpdate) {
        onImageUpdate(recipe.recipe_id, null, null);
      }
    } catch {
      toast.error(fr ? 'Erreur' : 'Error');
    }
  }, [recipe?.recipe_id, fr, onImageUpdate]);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) handleUploadImage(file);
    if (e.target) e.target.value = '';
  }, [handleUploadImage]);

  // Placeholder controls
  const PlaceholderControls = () => (
    showControls && recipe?.recipe_id && (
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleGenerateImage(); }} disabled={isGenerating} className="text-xs h-7 px-2 text-white hover:bg-white/20" title={fr ? 'Generer une image IA' : 'Generate AI image'}>
          {isGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
        </Button>
        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleGetStockImage(); }} disabled={isGenerating} className="text-xs h-7 px-2 text-white hover:bg-white/20" title={fr ? 'Image stock' : 'Stock image'}>
          <Image className="w-3 h-3" />
        </Button>
        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }} disabled={isUploading} className="text-xs h-7 px-2 text-white hover:bg-white/20" title={fr ? 'Uploader photo' : 'Upload photo'}>
          {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
        </Button>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
      </div>
    )
  );

  // ================ RENDER STATES ================

  // 1. GENERATING — show tactical loading
  if (imageStatus === 'generating' || (isGenerating && !fullImageUrl)) {
    return (
      <div className={`${sizeClasses[size]} bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-lg flex flex-col items-center justify-center gap-2 relative overflow-hidden`} data-testid="recipe-image-generating">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 via-red-500/10 to-orange-500/5 animate-pulse" />
        <div className="relative flex flex-col items-center gap-2">
          <div className="relative">
            <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
            <Sparkles className="w-3 h-3 text-amber-400 absolute -top-1 -right-1 animate-pulse" />
          </div>
          <p className="text-[10px] text-orange-300 font-heading uppercase tracking-wider text-center px-4">
            {fr ? 'En cuisine : Generation du visuel...' : 'In kitchen: Generating visual...'}
          </p>
        </div>
      </div>
    );
  }

  // 2. EMPTY / ERROR — FALLBACK IMPLACABLE. Le vide est INTERDIT.
  if (displayState === 'empty' || displayState === 'error') {
    const fallbackSrc = getFallbackImage(recipe?.title);
    return (
      <div className={`${sizeClasses[size]} relative rounded-lg overflow-hidden group bg-zinc-800`} data-testid="recipe-image-fallback">
        <img
          src={fallbackSrc}
          alt={recipe?.title || 'Recipe'}
          className="w-full h-full object-cover opacity-50"
          loading="lazy"
          onError={(e) => {
            // If even the fallback fails, try next one
            const nextIdx = (fallbackIndex + 1) % FALLBACK_IMAGES.length;
            if (nextIdx !== fallbackIndex) {
              setFallbackIndex(nextIdx);
              e.target.src = FALLBACK_IMAGES[nextIdx];
            }
          }}
        />
        {/* Overlay with icon + controls */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col items-center justify-end pb-3 gap-1.5">
          <UtensilsCrossed className="w-5 h-5 text-white/40 mb-1" />
          <PlaceholderControls />
        </div>
      </div>
    );
  }

  // 3. LOADING / LOADED — show actual image with skeleton
  return (
    <div ref={containerRef} className={`${sizeClasses[size]} relative rounded-lg overflow-hidden group bg-zinc-800`} data-testid="recipe-image-container">
      {/* Skeleton placeholder — visible until image loads */}
      <div 
        className={`absolute inset-0 z-10 transition-opacity duration-500 ${
          displayState === 'loaded' ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        {/* Show fallback food image as skeleton background instead of grey */}
        <img
          src={getFallbackImage(recipe?.title)}
          alt=""
          className="w-full h-full object-cover opacity-30"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-pulse" />
        {displayState === 'loading' && isVisible && fullImageUrl && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-white/30 animate-spin" />
          </div>
        )}
      </div>
      
      {/* Actual image — only render when visible */}
      {fullImageUrl && isVisible && (
        <img 
          ref={imgRef}
          src={fullImageUrl}
          alt={recipe?.title || 'Recipe'}
          className={`w-full h-full object-cover transition-opacity duration-500 ${
            displayState === 'loaded' ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={handleImageLoad}
          onError={handleImageError}
          decoding="async"
        />
      )}
      
      {/* Hover controls when loaded */}
      {showControls && recipe?.recipe_id && displayState === 'loaded' && (
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 z-20">
          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleGenerateImage(); }} disabled={isGenerating} className="text-white hover:bg-white/20 h-8 px-2" title={fr ? "Regenerer l'image" : 'Regenerate image'}>
            {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }} disabled={isUploading} className="text-white hover:bg-white/20 h-8 px-2" title={fr ? 'Uploader ma photo' : 'Upload my photo'}>
            {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }} className="text-red-400 hover:bg-red-500/20 h-8 px-2" title={fr ? 'Supprimer image' : 'Remove image'}>
            <Trash2 className="w-4 h-4" />
          </Button>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
        </div>
      )}
      
      {/* Source badge */}
      {recipe?.image_source && displayState === 'loaded' && (
        <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/50 text-white text-[10px] rounded z-10">
          {recipe.image_source === 'ai_generated' ? 'Auto' : recipe.image_source === 'user_upload' ? 'Ma photo' : 'Stock'}
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.recipe?.recipe_id === nextProps.recipe?.recipe_id &&
    prevProps.recipe?.image_url === nextProps.recipe?.image_url &&
    prevProps.recipe?.image_source === nextProps.recipe?.image_source &&
    prevProps.recipe?.image_status === nextProps.recipe?.image_status &&
    prevProps.size === nextProps.size &&
    prevProps.showControls === nextProps.showControls &&
    prevProps.language === nextProps.language
  );
});

RecipeImage.displayName = 'RecipeImage';

export default RecipeImage;
